import { useMemo } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import { validate as uuidValidate } from 'uuid';

import { userEndpoints as endpoints } from '@/api/endpoints';
import { callGet, callPut } from '@/api/fetcher';
import { updateUser as updateUserAction } from '@/redux/user/actions';
import { selectIsAuthenticated } from '@/redux/user/selectors';
import { User } from '@/shared/types/user';
import { UI_LANGUAGES } from '@/util/constants';
import { formatLanguageLabel, prioritizeLanguageOptions } from '@/util/languageOptions';

export const API = {
  getAgent: async (id: string): Promise<User> => callGet(endpoints.agent(id)),
  getUser: async (): Promise<User> => callGet(endpoints.user),
  updateUser: async (user: Partial<User>): Promise<User> => callPut(endpoints.user, user),
};

/**
 * Custom hook for user management with optimized re-render prevention
 *
 * @param id - Optional user/agent ID for fetching specific agent data
 * @returns Memoized object containing user data, mutations, and utility functions
 *
 * Performance Optimizations:
 * - Memoized mutation callbacks to prevent function recreation
 * - Memoized query configurations and enabled conditions
 * - Memoized return object to prevent unnecessary re-renders in consuming components
 * - Stable references for all functions and computed values
 *
 * @example
 * ```tsx
 * const MyComponent = React.memo(() => {
 *   const { user, updateUser } = useUser();
 *   // Component will only re-render when user data actually changes
 *   return <div>{user?.name}</div>;
 * });
 * ```
 */
const useUser = (id?: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const dispatch = useDispatch();
  const { data: user, status: getUserStatus } = useQuery<User | null>({
    queryKey: [endpoints.user],
    queryFn: API.getUser,
    enabled: isAuthenticated,
  });

  const { data: agent, status: getAgentStatus } = useQuery<User>({
    queryKey: [endpoints.agent(id)],
    queryFn: () => API.getAgent(id),
    enabled: isAuthenticated && !!id && uuidValidate(id),
  });

  const {
    mutate: updateUser,
    mutateAsync: updateUserAsync,
    status: updateStatus,
  } = useMutation<User, Error, Partial<User>>({
    mutationFn: API.updateUser,
    onSuccess: (resp) => {
      queryClient.setQueryData<User>([endpoints.user], (prev) => ({ ...prev, ...resp }));
      dispatch(updateUserAction(resp));
      toast.success(t('user.user_updated'));
    },
    onError: () => {
      toast.error(t('common.could_not_save_changes'));
    },
  });

  const supportedLanguageOptions = useMemo(
    () =>
      prioritizeLanguageOptions(
        UI_LANGUAGES.map((lang) => ({ value: lang, label: formatLanguageLabel(lang) }))
      ),
    []
  );

  return {
    user,
    getUserStatus,
    agent,
    getAgentStatus,
    updateUser,
    updateUserAsync,
    updateStatus,
    supportedLanguageOptions,
  };
};

export default useUser;
